import { telefonoParaMostrar } from "../../../helpers/telefonoWhatsapp";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { Row, Col } from "react-bootstrap";
import SpinLoading from "../../../components/spinLoading/SpinLoading";
import { handleError } from "../../../helpers/handleError";
import OrganizerPillSelect from "../../components/OrganizerPillSelect";
import { whatsappHref } from "../../components/Whatsapp";
import { EFDatePill } from "../../components/AgendaDatePill";
import AgendaToolbar, { AgendaViewTabs } from "../../components/AgendaToolbar";
import {
	organizerListReservationsApi,
	organizerConfirmReservationApi,
	organizerCancelReservationApi,
	organizerNoShowReservationApi,
	organizerManualCheckinReservationApi,
	organizerDeleteReservationBlockApi,
	organizerCancelReservationSeriesApi,
	organizerCancelOpenMatchApi,
} from "../../../api/api";
import { SPORT_LABELS } from "../../../constants/courtDomain";
import NewInternalReservationModal from "./NewInternalReservationModal";
import NewCourtBlockModal from "./NewCourtBlockModal";
import CourtSeriesListModal from "./CourtSeriesListModal";
import BirthdayInboxLink from "./BirthdayInboxLink";
import RecordatorioLineas from "../../recordatorios/RecordatorioLineas";
import CourtSeriesKeptReport from "./CourtSeriesKeptReport";
import CourtReservationDetailRows from "./CourtReservationDetailRows";
import VerFichaReserva from "./VerFichaReserva";
import { countOpenMatches, isOpenMatchRow, pillLabel } from "./openMatchModel";
import { duracionLabel } from "../../turnos/agenda/agendaLayout";
import {
	seriesCancelledMessage,
	seriesCreatedMessage,
} from "./courtSeriesModel";
import OrganizerDailyCourtGrid from "./OrganizerDailyCourtGrid";
import ListaEsperaDelDia from "../../listaEspera/ListaEsperaDelDia";
import { isWaitlistEnabled } from "../../../pages/listaDeEspera/waitlistModel";
import { useOrganizerDailySchedule } from "./useOrganizerDailySchedule";
import {
	formatAxisMinute,
	isCurrentFreeSelection,
} from "./dailyCourtGridLayout";
import {
	blockGroupRange,
	blockGroupSize,
	blockTypeMeta,
} from "./courtBlockModel";

/* ─────────────────────────────────────────
   OrgReservationsScreen — extracted verbatim from
   src/organizer/reservas/ReservasOrganizer.jsx (Unit 7 / PR7, task 7.2) so it
   can be shared by both the bar organizer (ReservasOrganizer.jsx, default
   `variant="bar"`) and the court organizer (CanchasOrganizer.jsx,
   `variant="club"`, task 7.4). Task 7.2 was a pure, behavior-preserving move
   (no logic changes) — see ReservasOrganizer.characterization.test.jsx
   (task 7.1) for the regression-guard suite that proved it. Task 7.3
   (TDD RED->GREEN, see OrgReservationsScreen.test.jsx) added the `variant`
   prop's column projection (Zona/Pers. for bars vs. Cancha/Duración for
   clubs) and wired the previously-dead "Cancelar" action
   (organizerCancelReservationApi) for both variants.
───────────────────────────────────────── */

/* Duplicated intentionally (matches the existing getCleanErrorMessage
   duplication between ReservasOrganizer.jsx and CanchasOrganizer.jsx) rather
   than exported/shared, to keep this extraction a minimal, isolated move —
   ReservasOrganizer.jsx's own copy (still used by its unrelated handleSave)
   is untouched. */
function getCleanErrorMessage(error) {
	if (!error) return "";
	if (error.response?.data) {
		const data = error.response.data;
		if (typeof data === "string") return data;
		if (data.message) {
			let msg = data.message;
			if (data.errors) {
				if (Array.isArray(data.errors)) {
					msg +=
						"\n" +
						data.errors
							.map((e) =>
								typeof e === "string" ? e : e.message || JSON.stringify(e),
							)
							.join("\n");
				} else if (typeof data.errors === "object") {
					msg +=
						"\n" +
						Object.entries(data.errors)
							.map(([k, v]) => `- ${k}: ${v}`)
							.join("\n");
				}
			}
			return msg;
		}
	}
	return error.message || "Ocurrió un error inesperado. Intenta nuevamente.";
}

function isPastReservation(r) {
	if (!r || !r.date) return false;
	const dateStr = dayjs(r.date).format("YYYY-MM-DD");
	const timeStr = r.slotEnd || r.slotStart;
	if (!timeStr) {
		return dayjs().isAfter(dayjs(dateStr).endOf("day"));
	}
	const parts = String(timeStr).split(":");
	const h = parseInt(parts[0] || "0", 10);
	const m = parseInt(parts[1] || "0", 10);
	let resDateTime = dayjs(dateStr).hour(h).minute(m).second(0);

	if (r.slotStart && r.slotEnd) {
		const startH = parseInt(String(r.slotStart).split(":")[0] || "0", 10);
		if (h < startH) {
			resDateTime = resDateTime.add(1, "day");
		}
	}

	return dayjs().isAfter(resDateTime);
}

const isReservationPresent = (reservation) =>
	Boolean(reservation?.checkedInAt) ||
	reservation?.status === "present" ||
	reservation?.checkedIn === true ||
	reservation?.checkIn === true ||
	Boolean(reservation?.checkInAt) ||
	Boolean(reservation?.checkinAt);

const DAYS_OF_WEEK_ES = [
	"Domingo",
	"Lunes",
	"Martes",
	"Miércoles",
	"Jueves",
	"Viernes",
	"Sábado",
];

function formatDateWithDay(dateVal) {
	if (!dateVal) return "-";
	const d = dayjs(dateVal);
	if (!d.isValid()) return "-";
	const dayName = DAYS_OF_WEEK_ES[d.day()];
	return `${dayName} ${d.format("DD/MM/YYYY")}`;
}

/* ─────────────────────────────────────────
   OrgReservationsScreen
───────────────────────────────────────── */
function OrgReservationsScreen({
	bar,
	onBack,
	setToastMessage,
	defaultViewMode,
	variant = "bar",
}) {
	const isClub = variant === "club";

	const [reservations, setReservations] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [viewMode, setViewMode] = useState(() => defaultViewMode || "grid");
	const [gridDate, setGridDate] = useState(() => dayjs().format("YYYY-MM-DD"));
	const [showCancelled, setShowCancelled] = useState(false);

	const dailySchedule = useOrganizerDailySchedule({
		clubId: bar._id,
		date: gridDate,
		includeCancelled: showCancelled,
		enabled: isClub && viewMode === "grid",
	});
	const refreshDailySchedule =
		dailySchedule.reloadAndWait || dailySchedule.reload;

	const enrichedSchedule = useMemo(() => {
		if (!dailySchedule.schedule) return dailySchedule.schedule;
		const scheduleReservations = dailySchedule.schedule.reservations;
		if (!Array.isArray(scheduleReservations) || !reservations?.length) {
			return dailySchedule.schedule;
		}
		const reservationsMap = new Map(
			reservations.map((res) => [String(res._id || res.id), res]),
		);
		let hasChanges = false;
		const nextReservations = scheduleReservations.map((item) => {
			const matched = reservationsMap.get(String(item.id));
			if (!matched) return item;
			const isMatchPresent = isReservationPresent(matched);
			const isItemPresent = isReservationPresent(item);
			if (
				isMatchPresent !== isItemPresent ||
				(matched.checkedInAt && matched.checkedInAt !== item.checkedInAt) ||
				(matched.status === "present" && item.status !== "present")
			) {
				hasChanges = true;
				return {
					...item,
					checkedInAt:
						matched.checkedInAt ||
						item.checkedInAt ||
						(matched.status === "present" ? new Date().toISOString() : undefined),
					status: matched.status === "present" ? "present" : item.status,
					canCheckIn:
						typeof matched.canCheckIn === "boolean"
							? matched.canCheckIn
							: item.canCheckIn,
				};
			}
			return item;
		});
		return hasChanges
			? { ...dailySchedule.schedule, reservations: nextReservations }
			: dailySchedule.schedule;
	}, [dailySchedule.schedule, reservations]);

	const statusOptions = [
		{ value: "all", label: "Todos" },
		{ value: "pending", label: "Pendientes" },
		{ value: "confirmed", label: "Confirmadas" },
		{ value: "cancelled", label: "Canceladas" },
		{ value: "no_show", label: "No Show" },
		// Not a real status: keeps the rows that carry an open match (see isOpenMatchRow).
		{ value: "open_match", label: "Partidos abiertos" },
	];

	// Filters
	const [dateFilter, setDateFilter] = useState(dayjs().format("YYYY-MM-DD"));
	const [statusFilter, setStatusFilter] = useState("all");
	const [searchQuery, setSearchQuery] = useState("");

	// Sorting & Pagination
	const [currentPage, setCurrentPage] = useState(1);
	const itemsPerPage = 10;

	// Reset pagination on filter changes
	useEffect(() => {
		setCurrentPage(1);
	}, [dateFilter, statusFilter, searchQuery]);

	// Action confirmations
	const [fichaDe, setFichaDe] = useState(null); // id de la reserva cuya ficha de cliente está abierta
	const [actionConfirm, setActionConfirm] = useState(null); // { type: 'cancel' | 'noshow', reservation: obj }
	const [actionLoading, setActionLoading] = useState(false);

	// Task 7.6 (Unit 6): "Nueva reserva" entry point, club-only. Grid
	// selections reuse this same modal state with validated prefill values.
	const [newReservationPrefill, setNewReservationPrefill] = useState(null);

	// "Bloquear canchas" (club grid toolbar): typed block of one, several or all
	// courts over one or more days. It needs the grid's courts, so it lives in
	// the grid view.
	const [blockModalOpen, setBlockModalOpen] = useState(false);

	// "Turnos fijos" (club grid toolbar): the club's active weekly series, with
	// a cancel-from-today action. Like blocks, it lives in the grid view.
	const [seriesListOpen, setSeriesListOpen] = useState(false);

	const loadReservations = useCallback(async () => {
		try {
			setLoading(true);
			setError("");

			// The server defaults to scope "upcoming" when neither `scope` nor
			// `date` is sent, which hides every elapsed reservation. This screen
			// needs the full history: the date filter is applied client-side and
			// the summary metrics are global, so both depend on the whole set.
			const params = { barId: bar._id, scope: "all" };
			const res = await organizerListReservationsApi(params);
			setReservations(Array.isArray(res?.data?.info) ? res.data.info : []);
		} catch (err) {
			setError(getCleanErrorMessage(err));
		} finally {
			setLoading(false);
		}
	}, [bar._id]);

	useEffect(() => {
		loadReservations();
	}, [loadReservations]);

	// Client-side date, status, and text search filter
	const filteredReservations = React.useMemo(() => {
		return reservations.filter((r) => {
			// Exclude holding and expired statuses from organizer view completely
			if (r.status === "holding" || r.status === "expired") return false;

			// Exclude temporary holds/empty code rows
			if (!r.reservationCode) return false;

			// 1. Date filter
			if (dateFilter) {
				const rDate = r.date ? dayjs(r.date).format("YYYY-MM-DD") : "";
				if (rDate !== dateFilter) return false;
			}

			// 2. Status filter
			if (statusFilter === "open_match") {
				if (!isOpenMatchRow(r)) return false;
			} else if (statusFilter !== "all" && r.status !== statusFilter) {
				return false;
			}

			// 3. Search query
			if (searchQuery.trim()) {
				const query = searchQuery.toLowerCase();
				const matchesSearch =
					r.name?.toLowerCase().includes(query) ||
					r.email?.toLowerCase().includes(query) ||
					r.dni?.includes(query) ||
					r.reservationCode?.toString().includes(query);
				if (!matchesSearch) return false;
			}

			return true;
		});
	}, [reservations, dateFilter, statusFilter, searchQuery]);

	// Client-side sorting
	const sortedReservations = React.useMemo(() => {
		const list = [...filteredReservations];
		// Sort by date ascending, then slotStart (horario) ascending
		list.sort((a, b) => {
			const dateA = a.date ? dayjs(a.date).format("YYYY-MM-DD") : "";
			const dateB = b.date ? dayjs(b.date).format("YYYY-MM-DD") : "";
			const cmpDate = dateA.localeCompare(dateB);
			if (cmpDate !== 0) return cmpDate;
			return (a.slotStart || "").localeCompare(b.slotStart || "");
		});
		return list;
	}, [filteredReservations]);

	const totalItems = sortedReservations.length;
	const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;

	const paginatedReservations = React.useMemo(() => {
		return sortedReservations.slice(
			(currentPage - 1) * itemsPerPage,
			currentPage * itemsPerPage,
		);
	}, [sortedReservations, currentPage]);

	// Action Handlers
	const handleConfirm = async (id, refreshGrid = false) => {
		try {
			setActionLoading(true);
			await organizerConfirmReservationApi(id);
			if (refreshGrid) await refreshDailySchedule();
			setToastMessage("Reserva confirmada correctamente.");
			setActionConfirm(null);
			loadReservations();
		} catch (err) {
			handleError(err);
		} finally {
			setActionLoading(false);
		}
	};

	// `blockScope: "group"` deletes the whole block (every court and day created
	// together); otherwise only the selected row goes.
	// Date (YYYY-MM-DD) a "this and the following" cancel starts from, or null
	// when it is unknown: sending no date would make the backend start from
	// today, which can cancel more than the organizer chose. The grid's day is
	// authoritative for grid selections; the list rows carry a plain date string.
	const seriesCancelFrom = (reservation, origin) => {
		if (origin === "grid") return gridDate;
		const date = String(reservation?.date || "").slice(0, 10);
		return /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : null;
	};

	// `seriesScope: "following"` cancels a turno fijo's date and every later one
	// (series cancel from that date); otherwise only the selected reservation goes.
	const handleActionExecute = async ({ blockScope, seriesScope } = {}) => {
		if (!actionConfirm) return;
		const { type, reservation: initialReservation, origin } = actionConfirm;
		const reservation = initialReservation
			? getLatestReservation(initialReservation)
			: null;
		if (
			(type === "cancel" || type === "noshow" || type === "checkin") &&
			!reservation
		)
			return;
		if (
			(type === "cancel" || type === "noshow") &&
			isReservationPresent(reservation)
		) {
			if (origin === "grid") await refreshDailySchedule();
			setActionConfirm(null);
			await loadReservations();
			setToastMessage("La reserva ya figura como Presente.");
			return;
		}
		// What replaces the confirmation once the action succeeded: nothing, or the
		// report of the dates a series cancel had to keep.
		let nextConfirm = null;
		try {
			setActionLoading(true);
			if (type === "cancel") {
				const seriesFrom = seriesCancelFrom(reservation, origin);
				if (seriesScope === "following" && reservation.seriesId && seriesFrom) {
					const response = await organizerCancelReservationSeriesApi(
						reservation.seriesId,
						seriesFrom,
					);
					const result = response?.data?.info || {};
					setToastMessage(seriesCancelledMessage(result));
					if (Array.isArray(result.kept) && result.kept.length > 0) {
						nextConfirm = { type: "seriesReport", result };
					}
				} else {
					await organizerCancelReservationApi(reservation._id);
					setToastMessage("Reserva cancelada correctamente.");
				}
			} else if (type === "cancelMatch") {
				// Frees the court slot and notifies the players; idempotent on the back.
				const response = await organizerCancelOpenMatchApi(reservation.openMatch._id);
				setToastMessage(
					response?.data?.info?.alreadyCancelled
						? "El partido abierto ya estaba cancelado."
						: "Partido abierto cancelado correctamente.",
				);
			} else if (type === "noshow") {
				await organizerNoShowReservationApi(reservation._id);
				setToastMessage("Reserva marcada como No Show.");
			} else if (type === "checkin") {
				const response = await organizerManualCheckinReservationApi(
					reservation._id,
				);
				const payload = response?.data;
				if (payload?.isSuccess !== true) {
					if (origin === "grid") await refreshDailySchedule();
					setActionConfirm(null);
					await loadReservations();
					setToastMessage(
						payload?.message || "La reserva ya fue marcada como presente.",
					);
					return;
				}
				setToastMessage("Ingreso marcado correctamente.");
			} else if (type === "block") {
				if (blockScope === "group") {
					const response = await organizerDeleteReservationBlockApi(
						actionConfirm.block.id,
						{ scope: "group" },
					);
					const deleted =
						response?.data?.info?.deletedCount ||
						blockGroupSize(actionConfirm.block);
					setToastMessage(
						`Bloqueo eliminado correctamente (${deleted} en total).`,
					);
				} else {
					await organizerDeleteReservationBlockApi(actionConfirm.block.id);
					setToastMessage("Bloqueo eliminado correctamente.");
				}
			}
			if (origin === "grid") await refreshDailySchedule();
			setActionConfirm(nextConfirm);
			if (type !== "block") await loadReservations();
		} catch (err) {
			if (type === "checkin" && err?.response?.status === 403) {
				if (origin === "grid") await refreshDailySchedule();
				await loadReservations();
				setToastMessage(
					err.response?.data?.message ||
						"No se puede marcar el ingreso fuera de la ventana permitida.",
				);
			} else {
				handleError(err);
			}
		} finally {
			setActionLoading(false);
		}
	};

	const handleReservationCreated = async () => {
		await refreshDailySchedule();
		setToastMessage("Reserva creada correctamente.");
		loadReservations();
	};

	// The series endpoint already booked what it could; a failed refresh only
	// leaves the grid in its own error state with a retry button.
	const handleSeriesCreated = async (result) => {
		try {
			await refreshDailySchedule();
		} catch {
			// handled by the grid
		}
		setToastMessage(seriesCreatedMessage(result));
		loadReservations();
	};

	// Cancelled from the series list: refresh what the grid and the table show.
	// The list modal keeps the report of dates that were kept.
	const handleSeriesCancelled = async (result) => {
		try {
			await refreshDailySchedule();
		} catch {
			// handled by the grid
		}
		setToastMessage(seriesCancelledMessage(result));
		loadReservations();
	};

	const handleBlockCreated = async (result) => {
		// A failed refresh leaves the grid in its own error state with a retry
		// button; the block already exists, so the modal must not report a failure.
		try {
			await refreshDailySchedule();
		} catch {
			// handled by the grid
		}
		const count = Number(result?.count) || 0;
		setToastMessage(
			count > 1
				? `Bloqueo creado correctamente (${count} en total).`
				: "Bloqueo creado correctamente.",
		);
	};

	const handleGridFreeSelect = (selection) => {
		if (!isCurrentFreeSelection(dailySchedule.schedule, selection)) return;
		setNewReservationPrefill({
			origin: "grid",
			date: selection.date,
			courtId: selection.courtId,
			slotStart: formatAxisMinute(selection.startMin).slice(0, 5),
		});
	};

	const handleGridReservationSelect = (selection) => {
		const reservationFromList = reservations.find(
			(item) => String(item._id) === String(selection.id),
		);
		const reservation = reservationFromList
			? { ...selection, ...reservationFromList }
			: selection;
		if (reservation?._id || reservation?.id)
			setActionConfirm({ type: "detail", reservation, origin: "grid" });
	};

	const handleGridBlockSelect = (block) => {
		if (block?.id) setActionConfirm({ type: "block", block, origin: "grid" });
	};

	// Status map for badges
	const STATUS_DETAILS = {
		holding: { label: "Retenida", bg: "#f3f4f6", color: "#6b7280" },
		pending: { label: "Pendiente", bg: "#fef3c7", color: "#d97706" },
		reserved: { label: "Próxima", bg: "#dbeafe", color: "#2563eb" },
		confirmed: { label: "Confirmada", bg: "#f5f7e6", color: "#b1c20e" },
		cancelled: { label: "Cancelada", bg: "#fee2e2", color: "#dc2626" },
		expired: { label: "Expirada", bg: "#f3f4f6", color: "#9ca3af" },
		no_show: { label: "No Show", bg: "#ffedd5", color: "#ea580c" },
		present: { label: "Presente", bg: "var(--surface-2)", color: "var(--text)" },
	};

	const getReservationViewState = (reservation) => {
		const isPast = isPastReservation(reservation);
		const isTerminal = ["cancelled", "expired", "no_show"].includes(
			reservation.status,
		);
		const isCheckedIn = isReservationPresent(reservation);
		const hasBackendCheckInFlag = typeof reservation.canCheckIn === "boolean";
		const isLegacyCheckInEligible =
			["reserved", "confirmed"].includes(reservation.status) && !isPast;

		return {
			badge: isCheckedIn
				? STATUS_DETAILS.present
				: STATUS_DETAILS[reservation.status] || {
						label: reservation.status,
						bg: "var(--surface-2)",
						color: "var(--text)",
				  },
			isCheckedIn,
			isPending:
				reservation.status === "pending" || reservation.status === "reserved",
			isTerminal,
			isPast,
			isCheckInEligible:
				isClub &&
				!isCheckedIn &&
				!isTerminal &&
				(hasBackendCheckInFlag
					? reservation.canCheckIn
					: isLegacyCheckInEligible),
		};
	};

	const getLatestReservation = (reservation) => {
		const latestReservation = reservations.find(
			(item) => String(item._id) === String(reservation?._id || reservation?.id),
		);
		return latestReservation
			? { ...reservation, ...latestReservation }
			: reservation;
	};

	const actionReservation = actionConfirm?.reservation
		? getLatestReservation(actionConfirm.reservation)
		: null;
	const actionReservationIsPresent = isReservationPresent(actionReservation);
	// A cancel of a turno fijo date offers "only this" or "this and the
	// following"; the latter needs the date it starts from.
	const actionOffersSeriesCancel =
		actionConfirm?.type === "cancel" &&
		Boolean(actionReservation?.seriesId) &&
		Boolean(seriesCancelFrom(actionReservation, actionConfirm.origin));
	// Rows created together with the selected block (1 when it stands alone).
	const actionBlockGroupSize =
		actionConfirm?.type === "block" ? blockGroupSize(actionConfirm.block) : 1;
	const selectedReservation =
		actionConfirm?.type === "detail" && actionConfirm.reservation
			? getLatestReservation(actionConfirm.reservation)
			: actionConfirm?.reservation;
	const selectedReservationViewState = selectedReservation
		? getReservationViewState(selectedReservation)
		: null;
	const detailBadgeClass = selectedReservationViewState?.isCheckedIn
		? " zr-badge--present"
		: ["pending", "reserved"].includes(selectedReservation?.status)
			? " zr-badge--pending"
			: ["cancelled", "no_show"].includes(selectedReservation?.status)
				? " zr-badge--cancel"
				: "";

	// Summary Metrics calculated from all reservations of the bar (unfiltered by date/status/search, excluding holds and expired)
	const metrics = React.useMemo(() => {
		const summary = {
			total: 0,
			confirmed: 0,
			pending: 0,
			noShow: 0,
			cancelled: 0,
			openMatches: 0,
		};
		reservations.forEach((r) => {
			if (r.status === "holding" || r.status === "expired") return; // skip holds and expired in metrics
			if (!r.reservationCode) return; // skip holds/empty code rows
			summary.total++;
			if (r.status === "confirmed") summary.confirmed++;
			else if (r.status === "pending" || r.status === "reserved")
				summary.pending++;
			else if (r.status === "no_show") summary.noShow++;
			else if (r.status === "cancelled") summary.cancelled++;
		});
		summary.openMatches = countOpenMatches(
			reservations.filter(
				(r) => r.status !== "holding" && r.status !== "expired" && r.reservationCode,
			),
		);
		return summary;
	}, [reservations]);

	const formatMetric = (val) => (val > 0 ? String(val).padStart(2, "0") : "0");

	// Task 7.3 (additive, variant-aware column projection): "Zona"/"Pers." for
	// bars vs. "Cancha"/"Duración" for clubs. Bar behavior (variant="bar", the
	// default) is byte-identical to the pre-Unit-7 rendering.
	const locationColumnLabel = isClub ? "Cancha" : "Zona";
	const metaColumnLabel = isClub ? "Duración" : "Personas";
	const locationValue = (r) => {
		if (!isClub) return r.zone || "-";
		const sportLabel = r.sport ? SPORT_LABELS[r.sport] || r.sport : "";
		return [r.courtName, sportLabel].filter(Boolean).join(" · ") || "-";
	};
	const metaValue = (r) =>
		isClub
			? r.durationMinutes
				? `${r.durationMinutes} min`
				: "-"
			: r.partySize;

	return (
		<div className="org-reservations-screen" style={{ paddingBottom: "60px" }}>
			{/* Back bar */}
			<div className="zr-backbar">
				<button type="button" className="zr-btn zr-btn--soft" onClick={onBack}>
					<svg
						width="14"
						height="14"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="1.8"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<path d="M19 12H5M11 6l-6 6 6 6" />
					</svg>
					Volver
				</button>
				<div
					className="zr-subhead"
					style={{ display: "flex", alignItems: "baseline", gap: "8px" }}
				>
					<h2 className="zr-subhead__title">Reservas de {bar.name}</h2>
					<p className="zr-subhead__hint">
						| Visualiza y administra las reservas recibidas.
					</p>
				</div>
			</div>

			{isClub && (
				<AgendaViewTabs viewMode={viewMode} onChange={setViewMode} />
			)}

			{/* KPI cards */}
			<Row className="zr-kpis g-3">
				{[
					{
						key: "total",
						label: "Total",
						value: formatMetric(metrics.total),
						icon: (
							<svg
								width="21"
								height="21"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="1.8"
								strokeLinecap="round"
								strokeLinejoin="round"
							>
								<rect x="5" y="4.5" width="14" height="16" rx="2" />
								<path d="M9 4.5a1.5 1.5 0 0 1 1.5-1.5h3A1.5 1.5 0 0 1 15 4.5" />
								<path d="M8.5 10h7M8.5 13.5h7M8.5 17h4" />
							</svg>
						),
					},
					{
						key: "confirmed",
						label: "Confirmadas",
						value: formatMetric(metrics.confirmed),
						icon: (
							<svg
								width="21"
								height="21"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="1.8"
								strokeLinecap="round"
								strokeLinejoin="round"
							>
								<rect x="3" y="3" width="18" height="18" rx="4" />
								<path d="m9 11 2 2 4-4" />
							</svg>
						),
					},
					{
						key: "pending",
						label: "Pendientes",
						value: formatMetric(metrics.pending),
						icon: (
							<svg
								width="21"
								height="21"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="1.8"
								strokeLinecap="round"
								strokeLinejoin="round"
							>
								<circle cx="12" cy="12" r="9" />
								<path d="M12 7v5l3.5 2" />
							</svg>
						),
					},
					{
						key: "noShow",
						label: "No Show",
						value: formatMetric(metrics.noShow),
						icon: (
							<svg
								width="21"
								height="21"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="1.8"
								strokeLinecap="round"
								strokeLinejoin="round"
							>
								<circle cx="12" cy="12" r="9" />
								<path d="M12 7.5v5M12 16h.01" />
							</svg>
						),
					},
					{
						key: "cancelled",
						label: "Canceladas",
						value: formatMetric(metrics.cancelled),
						icon: (
							<svg
								width="21"
								height="21"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="1.8"
								strokeLinecap="round"
								strokeLinejoin="round"
							>
								<circle cx="12" cy="12" r="8.5" />
								<path d="m6 6 12 12" />
							</svg>
						),
					},
					// Falta un jugador: only clubs offer open matches.
					...(isClub
						? [
								{
									key: "openMatches",
									label: "Partidos abiertos",
									value: formatMetric(metrics.openMatches),
									icon: (
										<svg
											width="21"
											height="21"
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
											strokeWidth="1.8"
											strokeLinecap="round"
											strokeLinejoin="round"
										>
											<circle cx="9" cy="8" r="3" />
											<path d="M3 20v-1a5 5 0 0 1 5-5h2a5 5 0 0 1 5 5v1" />
											<path d="M18 8v6M15 11h6" />
										</svg>
									),
								},
							]
						: []),
				].map((k) => (
					<Col key={k.key}>
						<div className="zr-kpi">
							<span className="zr-kpi__icon">{k.icon}</span>
							<div>
								<div className="zr-kpi__label">{k.label}</div>
								<div className="zr-kpi__value">{k.value}</div>
							</div>
						</div>
					</Col>
				))}
			</Row>

			{viewMode === "grid" && isClub ? (
				<div className="odcg-panel">
					<AgendaToolbar
						date={gridDate}
						onDateChange={setGridDate}
						showCancelled={showCancelled}
						onShowCancelledChange={setShowCancelled}
						cancelledLabel="Mostrar reservas canceladas"
					>
						<button
							type="button"
							className="zr-btn zr-btn--dark zr-btn--new-res"
							onClick={() =>
								setNewReservationPrefill({
									origin: "grid",
									date: gridDate,
								})
							}
							aria-label="Nueva reserva"
						>
							<svg
								width="14"
								height="14"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="1.8"
								strokeLinecap="round"
								strokeLinejoin="round"
							>
								<path d="M12 5v14M5 12h14" />
							</svg>
							Nueva reserva
						</button>
						<button
							type="button"
							className="zr-btn zr-btn--soft"
							onClick={() => refreshDailySchedule()}
						>
							Actualizar agenda
						</button>
						<button
							type="button"
							className="zr-btn zr-btn--soft"
							onClick={() => setBlockModalOpen(true)}
						>
							Bloquear canchas
						</button>
						<button
							type="button"
							className="zr-btn zr-btn--soft"
							onClick={() => setSeriesListOpen(true)}
						>
							Turnos fijos
						</button>
						{isClub ? (
							<BirthdayInboxLink className="zr-btn zr-btn--soft">
								Cumpleaños
							</BirthdayInboxLink>
						) : null}
					</AgendaToolbar>
					<ListaEsperaDelDia
						enabled={isWaitlistEnabled(bar)}
						barId={bar._id}
						kind="court"
						date={gridDate}
						onToast={setToastMessage}
					/>
					<OrganizerDailyCourtGrid
						schedule={enrichedSchedule}
						state={dailySchedule.state}
						showCancelled={showCancelled}
						selectedReservationId={
							actionConfirm?.type === "detail" &&
							actionConfirm.origin === "grid"
								? actionConfirm.reservation?._id || actionConfirm.reservation?.id
								: null
						}
						onRetry={dailySchedule.reload}
						onFreeSelect={handleGridFreeSelect}
						onReservationSelect={handleGridReservationSelect}
						onBlockSelect={handleGridBlockSelect}
					/>
				</div>
			) : (
				<>
					{/* Filters */}
					<div className="zr-filters">
						<Row className="g-4 align-items-end">
							<Col md={isClub ? 3 : 3}>
								<div className="zr-field">
									<span className="zr-field__label">Fecha</span>
									<EFDatePill
										value={dateFilter}
										onChange={(d) =>
											setDateFilter(d ? d.format("YYYY-MM-DD") : "")
										}
										placeholder="dd/mm/aaaa"
									/>
								</div>
							</Col>
							<Col md={isClub ? 3 : 3}>
								<div className="zr-field">
									<span className="zr-field__label">Estado</span>
									<OrganizerPillSelect
										value={statusFilter}
										onChange={(val) => setStatusFilter(val)}
										options={statusOptions}
										placeholder="Todos"
									/>
								</div>
							</Col>
							<Col md={isClub ? 6 : 6}>
								<div className="zr-field">
									<span className="zr-field__label">Buscar cliente</span>
									<div className="zr-search-filter-wrap">
										<label
											className="zr-search"
											style={{ height: 40, flex: 1, marginBottom: 0 }}
										>
											<svg
												width="20"
												height="20"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												strokeWidth="1.8"
												strokeLinecap="round"
												strokeLinejoin="round"
											>
												<circle cx="11" cy="11" r="7" />
												<path d="m20 20-3.2-3.2" />
											</svg>
											<input
												type="text"
												placeholder="Buscar por nombre, email, DNI o código..."
												value={searchQuery}
												onChange={(e) => setSearchQuery(e.target.value)}
											/>
										</label>
										{isClub && (
											<button
												type="button"
												className="zr-btn zr-btn--dark zr-btn--new-res"
												style={{ height: 40 }}
												onClick={() => setNewReservationPrefill({})}
												aria-label="Nueva reserva"
											>
												<svg
													width="14"
													height="14"
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													strokeWidth="1.8"
													strokeLinecap="round"
													strokeLinejoin="round"
												>
													<path d="M12 5v14M5 12h14" />
												</svg>
												Nueva reserva
											</button>
										)}
									</div>
								</div>
							</Col>
						</Row>
					</div>

					{dateFilter && (
						<ListaEsperaDelDia
							enabled={isWaitlistEnabled(bar)}
							barId={bar._id}
							kind={isClub ? "court" : "table"}
							date={dateFilter}
							onToast={setToastMessage}
						/>
					)}

					{error && <div className="alert alert-danger">{error}</div>}

					{/* Table */}
					{loading ? (
						<div style={{ padding: "60px 0" }}>
							<SpinLoading />
						</div>
					) : filteredReservations.length === 0 ? (
						<div className="zr-panel" style={{ padding: 0 }}>
							<div className="zr-empty">
								No se encontraron reservas. Probá cambiando la fecha o el
								estado.
							</div>
						</div>
					) : (
						<div className="zr-table-wrap">
							<table className="zr-table zr-desktop-only">
								<thead>
									<tr>
										<th>Cód.</th>
										<th>Cliente</th>
										<th>Fecha/Hora</th>
										<th className="num">{metaColumnLabel}</th>
										<th>{locationColumnLabel}</th>
										<th>Pago / Seña</th>
										<th>Estado</th>
										<th className="right">Acciones</th>
									</tr>
								</thead>
								<tbody>
									{paginatedReservations.map((r) => {
										const {
											badge,
											isCheckedIn,
											isPending,
											isTerminal,
											isPast,
											isCheckInEligible,
										} = getReservationViewState(r);
										const isManual = r.bookingOrigin === "organizer";

										let badgeClass = "zr-badge";
										if (isCheckedIn)
											badgeClass = "zr-badge zr-badge--present";
										else if (r.status === "pending" || r.status === "reserved")
											badgeClass = "zr-badge zr-badge--pending";
										else if (r.status === "cancelled" || r.status === "no_show")
											badgeClass = "zr-badge zr-badge--cancel";

										return (
											<tr key={r._id}>
												<td className="code">#{r.reservationCode}</td>
												<td>
													<div className="zr-client">
														<div className="zr-client__name">
															{r.name || "-"}
														</div>
														<div className="zr-client__meta">
															{isManual ? (
																<>Tel: {telefonoParaMostrar(r.phone) || "-"}</>
															) : (
																<>
																	{r.email}
																	<br />
																	Tel: {telefonoParaMostrar(r.phone) || "-"} | DNI: {r.dni || "-"}
																</>
															)}
														</div>
														{r.notes && (
															<div
																style={{
																	fontSize: "12px",
																	fontWeight: "700",
																	color: "#b1c20e",
																	background: "#F6F9E8",
																	border: "1px solid rgba(143, 168, 10, 0.3)",
																	padding: "4px 8px",
																	borderRadius: "6px",
																	display: "inline-flex",
																	alignItems: "center",
																	gap: "4px",
																	marginTop: "6px",
																}}
															>
																💬 "{r.notes}"
															</div>
														)}
														<RecordatorioLineas reminder={r.reminder} className="zr-recordatorios--fila" />
													</div>
												</td>
												<td>
													<div style={{ fontWeight: 700, fontSize: 13 }}>
														{formatDateWithDay(r.date)}
													</div>
													<div style={{ fontWeight: 700, fontSize: 13 }}>
														{r.slotStart} {r.slotEnd ? `– ${r.slotEnd}` : ""} hs
													</div>
												</td>
												<td className="num" style={{ fontWeight: 700 }}>
													{metaValue(r)}
												</td>
												<td>{locationValue(r)}</td>
												<td className="muted">
													{r.mode === "deposit" ? (
														<>
															<div
																style={{ fontWeight: "700", color: "#111827" }}
															>
																$
																{Number(r.depositAmount || 0).toLocaleString(
																	"es-AR",
																)}
															</div>
															<div
																style={{
																	fontSize: "10px",
																	fontWeight: "800",
																	textTransform: "uppercase",
																	color:
																		r.paymentstatus === "paid"
																			? "#b1c20e"
																			: r.paymentstatus === "failed"
																				? "#dc2626"
																				: "#d97706",
																	marginTop: "2px",
																}}
															>
																{r.paymentstatus === "paid"
																	? "Abonado"
																	: r.paymentstatus === "failed"
																		? "Fallido"
																		: "Pendiente"}
															</div>
														</>
													) : (
														<span
															style={{ color: "#9ca3af", fontWeight: "700" }}
														>
															{isClub ? "En el club" : "En el bar"}
														</span>
													)}
												</td>
												<td>
													<span className={badgeClass}>
														<span className="dot" />
														{badge.label}
													</span>
													{isManual && (
														<span
															className="zr-badge zr-badge--origin"
															style={{ marginLeft: 6 }}
														>
															<span className="dot" />
															Carga manual
														</span>
													)}
													{isOpenMatchRow(r) && (
														<span
															className="zr-badge zr-badge--origin"
															style={{ marginLeft: 6 }}
														>
															<span className="dot" />
															{pillLabel(r.openMatch)}
														</span>
													)}
												</td>
												<td className="right">
													<div style={{ display: "inline-flex", gap: "6px" }}>
														{isPending && (
															<button
																type="button"
																className="zr-btn zr-btn--lime"
																style={{ height: 30, fontSize: 12 }}
																disabled={actionLoading}
																onClick={() => handleConfirm(r._id)}
															>
																Confirmar
															</button>
														)}
														{isCheckInEligible && (
															<button
																type="button"
																className="zr-pill-btn zr-pill-btn--dark zr-reservation-action"
																disabled={actionLoading}
																onClick={() =>
																	setActionConfirm({
																		type: "checkin",
																		reservation: r,
																	})
																}
															>
																Marcar ingreso
															</button>
														)}
														{!isCheckedIn && !isTerminal && !isPast && (
															<button
																type="button"
																className="zr-pill-btn"
																disabled={actionLoading}
																onClick={() =>
																	setActionConfirm({
																		type: "cancel",
																		reservation: r,
																	})
																}
															>
																Cancelar
															</button>
														)}
														{!isCheckedIn && !isTerminal && (
															<button
																type="button"
																className="zr-pill-btn"
																disabled={actionLoading}
																onClick={() =>
																	setActionConfirm({
																		type: "noshow",
																		reservation: r,
																	})
																}
															>
																No Show
															</button>
														)}
														{whatsappHref(r.phone, r.name) && (
															<a
																href={whatsappHref(r.phone, r.name)}
																target="_blank"
																rel="noreferrer"
																className="zr-pill-btn zr-pill-btn--whatsapp"
																style={{ textDecoration: "none"}}
															>
																<svg viewBox="0 0 24 24" fill="currentColor">
																	<path d="M20.52 3.48A11.935 11.935 0 0 0 12 0C5.373 0 0 5.373 0 12c0 2.111.548 4.182 1.588 6.01L0 24l6.153-1.566A11.93 11.93 0 0 0 12 24c6.627 0 12-5.373 12-12 0-3.19-1.244-6.188-3.48-8.52ZM12 22.001a9.96 9.96 0 0 1-5.08-1.393l-.364-.214-3.633.925.97-3.54-.237-.372A9.957 9.957 0 0 1 2 12C2 6.486 6.486 2 12 2c2.668 0 5.182 1.04 7.07 2.929C20.96 6.818 22 9.332 22 12c0 5.514-4.486 10.001-10 10.001Zm5.204-7.58c-.285-.143-1.684-.83-1.944-.924-.26-.095-.45-.143-.64.143-.19.285-.734.924-.9 1.114-.167.19-.334.214-.62.071-.285-.143-1.204-.444-2.293-1.417-.848-.756-1.42-1.69-1.587-1.975-.167-.285-.018-.439.125-.582.128-.128.285-.334.428-.5.143-.167.19-.285.285-.476.095-.19.047-.357-.024-.5-.071-.143-.64-1.54-.876-2.11-.23-.553-.465-.478-.64-.486-.167-.008-.357-.01-.547-.01-.19 0-.5.071-.762.357-.262.285-1.002.978-1.002 2.386s1.026 2.768 1.169 2.957c.143.19 2.02 3.086 4.89 4.325.683.294 1.215.47 1.63.603.685.218 1.31.188 1.803.114.55-.082 1.684-.69 1.922-1.357.238-.666.238-1.237.167-1.357-.071-.119-.262-.19-.547-.333Z" />
																</svg>
																Mensaje
															</a>
														)}
													</div>
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>

							{/* Mobile Card List View */}
							<div className="zr-mobile-only zr-res-cards-mobile">
								{paginatedReservations.map((r) => {
									const {
										badge,
										isCheckedIn,
										isPending,
										isTerminal,
										isPast,
										isCheckInEligible,
									} = getReservationViewState(r);
									const isManual = r.bookingOrigin === "organizer";

									let badgeClass = "zr-badge";
									if (isCheckedIn)
										badgeClass = "zr-badge zr-badge--present";
									else if (r.status === "pending" || r.status === "reserved")
										badgeClass = "zr-badge zr-badge--pending";
									else if (r.status === "cancelled" || r.status === "no_show")
										badgeClass = "zr-badge zr-badge--cancel";

									return (
										<div key={r._id} className="zr-res-card-mobile">
											<div className="zr-res-card-mobile__header">
												<span className="code">#{r.reservationCode}</span>
												<div className="zr-res-card-mobile__badges">
													<span className={badgeClass}>
														<span className="dot" />
														{badge.label}
													</span>
													{isManual && (
														<span className="zr-badge zr-badge--origin">
															<span className="dot" />
															Carga manual
														</span>
													)}
													{isOpenMatchRow(r) && (
														<span
															className="zr-badge zr-badge--origin"
														>
															<span className="dot" />
															{pillLabel(r.openMatch)}
														</span>
													)}
												</div>
											</div>
											<div className="zr-res-card-mobile__body">
												<div className="zr-client__name">{r.name || "-"}</div>
												<div className="zr-client__meta">
													{isManual ? (
														<>Tel: {telefonoParaMostrar(r.phone) || "-"}</>
													) : (
														<>
															{r.email}
															<br />
															Tel: {telefonoParaMostrar(r.phone) || "-"} | DNI: {r.dni || "-"}
														</>
													)}
												</div>
												{r.notes && (
													<div
														style={{
															fontSize: "11px",
															color: "#8FA80A",
															background: "#F6F9E8",
															border: "1px solid rgba(143, 168, 10, 0.3)",
															padding: "4px 8px",
															borderRadius: "6px",
															display: "inline-block",
															marginTop: "6px",
															fontWeight: "700",
														}}
													>
														💬 "{r.notes}"
													</div>
												)}
												<hr className="zr-card-divider" />
												<div className="zr-res-card-mobile__details">
													<div className="detail-item">
														<strong>Fecha:</strong> {formatDateWithDay(r.date)}
													</div>
													<div className="detail-item">
														<strong>Hora:</strong> {r.slotStart}{" "}
														{r.slotEnd ? `– ${r.slotEnd}` : ""} hs
													</div>
													<div className="detail-item">
														<strong>{metaColumnLabel}:</strong> {metaValue(r)}
													</div>
													<div className="detail-item">
														<strong>{locationColumnLabel}:</strong>{" "}
														{locationValue(r)}
													</div>
													<div className="detail-item">
														<strong>Pago:</strong>{" "}
														{r.mode === "deposit" ? (
															<span>
																$
																{Number(r.depositAmount || 0).toLocaleString(
																	"es-AR",
																)}{" "}
																(
																{r.paymentstatus === "paid"
																	? "Abonado"
																	: r.paymentstatus === "failed"
																		? "Fallido"
																		: "Pendiente"}
																)
															</span>
														) : (
															<span style={{ color: "#9ca3af" }}>
																{isClub ? "En el club" : "En el bar"}
															</span>
														)}
													</div>
													<RecordatorioLineas reminder={r.reminder} className="detail-item" />
												</div>
											</div>
											<div
												className="zr-res-card-mobile__footer"
												style={{ flexWrap: "wrap", gap: "8px" }}
											>
												{isPending && (
													<button
														type="button"
														className="zr-btn zr-btn--lime"
														style={{
															padding: "6px 12px",
															fontSize: 13,
															flex: "1 1 auto",
															whiteSpace: "nowrap",
														}}
														disabled={actionLoading}
														onClick={() => handleConfirm(r._id)}
													>
														Confirmar
													</button>
												)}
												{isCheckInEligible && (
													<button
														type="button"
														className="zr-pill-btn zr-pill-btn--dark zr-reservation-action"
														style={{
															padding: "6px 10px",
															fontSize: 13,
															flex: "1 1 auto",
															whiteSpace: "nowrap",
															textAlign: "center",
														}}
														disabled={actionLoading}
														onClick={() =>
															setActionConfirm({
																type: "checkin",
																reservation: r,
															})
														}
													>
														Marcar ingreso
													</button>
												)}
												{!isCheckedIn && !isTerminal && !isPast && (
													<button
														type="button"
														className="zr-pill-btn"
														style={{
															padding: "6px 10px",
															fontSize: 13,
															flex: "1 1 auto",
															whiteSpace: "nowrap",
															textAlign: "center",
														}}
														disabled={actionLoading}
														onClick={() =>
															setActionConfirm({
																type: "cancel",
																reservation: r,
															})
														}
													>
														Cancelar
													</button>
												)}
												{!isCheckedIn && !isTerminal && (
													<button
														type="button"
														className="zr-pill-btn"
														style={{
															padding: "6px 10px",
															fontSize: 13,
															flex: "1 1 auto",
															whiteSpace: "nowrap",
															textAlign: "center",
														}}
														disabled={actionLoading}
														onClick={() =>
															setActionConfirm({
																type: "noshow",
																reservation: r,
															})
														}
													>
														No Show
													</button>
												)}
												{whatsappHref(r.phone, r.name) && (
													<a
														href={whatsappHref(r.phone, r.name)}
														target="_blank"
														rel="noreferrer"
														className="zr-pill-btn zr-pill-btn--whatsapp"
														style={{
															padding: "6px 10px",
															fontSize: 13,
															flex: "1 1 auto",
															whiteSpace: "nowrap",
															textAlign: "center",
															textDecoration: "none",
														}}
													>
														<svg viewBox="0 0 24 24" fill="currentColor">
															<path d="M20.52 3.48A11.935 11.935 0 0 0 12 0C5.373 0 0 5.373 0 12c0 2.111.548 4.182 1.588 6.01L0 24l6.153-1.566A11.93 11.93 0 0 0 12 24c6.627 0 12-5.373 12-12 0-3.19-1.244-6.188-3.48-8.52ZM12 22.001a9.96 9.96 0 0 1-5.08-1.393l-.364-.214-3.633.925.97-3.54-.237-.372A9.957 9.957 0 0 1 2 12C2 6.486 6.486 2 12 2c2.668 0 5.182 1.04 7.07 2.929C20.96 6.818 22 9.332 22 12c0 5.514-4.486 10.001-10 10.001Zm5.204-7.58c-.285-.143-1.684-.83-1.944-.924-.26-.095-.45-.143-.64.143-.19.285-.734.924-.9 1.114-.167.19-.334.214-.62.071-.285-.143-1.204-.444-2.293-1.417-.848-.756-1.42-1.69-1.587-1.975-.167-.285-.018-.439.125-.582.128-.128.285-.334.428-.5.143-.167.19-.285.285-.476.095-.19.047-.357-.024-.5-.071-.143-.64-1.54-.876-2.11-.23-.553-.465-.478-.64-.486-.167-.008-.357-.01-.547-.01-.19 0-.5.071-.762.357-.262.285-1.002.978-1.002 2.386s1.026 2.768 1.169 2.957c.143.19 2.02 3.086 4.89 4.325.683.294 1.215.47 1.63.603.685.218 1.31.188 1.803.114.55-.082 1.684-.69 1.922-1.357.238-.666.238-1.237.167-1.357-.071-.119-.262-.19-.547-.333Z" />
														</svg>
														Mensaje
													</a>
												)}
											</div>
										</div>
									);
								})}
							</div>

							{/* Pagination */}
							{totalPages > 1 && (
								<div
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
										padding: "14px 20px",
										borderTop: "1px solid #f3f4f6",
										background: "#f9fafb",
										flexWrap: "wrap",
										gap: "10px",
									}}
								>
									<div
										style={{
											fontSize: "13px",
											fontWeight: "600",
											color: "#4b5563",
										}}
									>
										Mostrando{" "}
										<strong>
											{Math.min(
												(currentPage - 1) * itemsPerPage + 1,
												totalItems,
											)}
										</strong>{" "}
										a{" "}
										<strong>
											{Math.min(currentPage * itemsPerPage, totalItems)}
										</strong>{" "}
										de <strong>{totalItems}</strong> reservas
									</div>
									<div style={{ display: "flex", gap: "6px" }}>
										<button
											type="button"
											disabled={currentPage === 1}
											onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
											className="zr-btn zr-btn--soft"
											style={{ height: 32, fontSize: 13 }}
										>
											Anterior
										</button>
										{Array.from(
											{ length: totalPages },
											(_, idx) => idx + 1,
										).map((p) => {
											const isFirst = p === 1;
											const isLast = p === totalPages;
											const isNeighbor = Math.abs(p - currentPage) <= 1;
											if (!isFirst && !isLast && !isNeighbor) {
												if (p === 2 || p === totalPages - 1)
													return (
														<span
															key={p}
															style={{ padding: "6px", color: "#9ca3af" }}
														>
															...
														</span>
													);
												return null;
											}
											const active = p === currentPage;
											return (
												<button
													type="button"
													key={p}
													onClick={() => setCurrentPage(p)}
													className={
														active
															? "zr-btn zr-btn--dark"
															: "zr-btn zr-btn--soft"
													}
													style={{ height: 32, minWidth: 32, fontSize: 13 }}
												>
													{p}
												</button>
											);
										})}
										<button
											type="button"
											disabled={currentPage === totalPages}
											onClick={() =>
												setCurrentPage((p) => Math.min(totalPages, p + 1))
											}
											className="zr-btn zr-btn--soft"
											style={{ height: 32, fontSize: 13 }}
										>
											Siguiente
										</button>
									</div>
								</div>
							)}
						</div>
					)}
				</>
			)}

			{isClub && (
				<NewInternalReservationModal
					show={newReservationPrefill !== null}
					bar={bar}
					defaultDate={newReservationPrefill?.date}
					defaultCourtId={newReservationPrefill?.courtId}
					defaultSlotStart={newReservationPrefill?.slotStart}
					onHide={() => setNewReservationPrefill(null)}
					onCreated={handleReservationCreated}
					onSeriesCreated={handleSeriesCreated}
					onConflict={
						newReservationPrefill?.origin === "grid"
							? refreshDailySchedule
							: undefined
					}
				/>
			)}

			{isClub && (
				<NewCourtBlockModal
					show={blockModalOpen}
					bar={bar}
					courts={
						Array.isArray(enrichedSchedule?.courts) ? enrichedSchedule.courts : []
					}
					courtsState={dailySchedule.state}
					onRetryCourts={dailySchedule.reload}
					defaultDate={gridDate}
					onHide={() => setBlockModalOpen(false)}
					onCreated={handleBlockCreated}
				/>
			)}

			{isClub && (
				<CourtSeriesListModal
					show={seriesListOpen}
					bar={bar}
					onHide={() => setSeriesListOpen(false)}
					onCancelled={handleSeriesCancelled}
				/>
			)}

			{fichaDe && (
				<VerFichaReserva
					reservationId={fichaDe}
					onVolver={() => setFichaDe(null)}
					onClose={() => {
						setFichaDe(null);
						setActionConfirm(null);
					}}
				/>
			)}

			{/* Action confirm modal (No Show / Cancelar) */}
			{actionConfirm && !fichaDe && (
				<div className="zr-overlay" onMouseDown={() => setActionConfirm(null)}>
					<div
						className={`zr-modal${actionConfirm.type === "detail" ? " ba-modal ba-modal--detalle" : ""}`}
						role="dialog"
						aria-modal="true"
						aria-labelledby="zr-action-modal-title"
						onMouseDown={(e) => e.stopPropagation()}
					>
						{actionConfirm.type === "detail" && (
							<button
								type="button"
								className="ba-modal__close"
								aria-label="Cerrar detalle de la reserva"
								onClick={() => setActionConfirm(null)}
							>
								<svg
									width="20"
									height="20"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									aria-hidden="true"
								>
									<path d="m6 6 12 12M18 6 6 18" />
								</svg>
							</button>
						)}
						<div className="zr-modal__icon">
							<svg
								width="56"
								height="56"
								viewBox="0 0 24 24"
								fill="currentColor"
								style={{ color: "var(--zr-lime)" }}
							>
								<path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm-1.5 5.5a1.5 1.5 0 0 1 3 0v6a1.5 1.5 0 0 1-3 0zM12 18.4a1.7 1.7 0 1 1 0-3.4 1.7 1.7 0 0 1 0 3.4z" />
							</svg>
						</div>
						{actionConfirm.type === "detail" ? (
							<>
								<h3 id="zr-action-modal-title" className="zr-modal__title">
									Detalle de reserva
								</h3>
								<div style={{ marginBottom: "16px" }}>
									{selectedReservationViewState?.badge?.label && (
										<span className={`zr-badge${detailBadgeClass}`}>
											<span className="dot" />
											{selectedReservationViewState.badge.label}
										</span>
									)}
									{selectedReservation.seriesId && (
										<span
											className="zr-badge zr-badge--series"
											style={{ marginLeft: 6 }}
										>
											<svg
												width="12"
												height="12"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												strokeWidth="2.5"
												strokeLinecap="round"
												strokeLinejoin="round"
												aria-hidden="true"
											>
												<path d="M17 2l4 4-4 4" />
												<path d="M3 11V9a3 3 0 0 1 3-3h15" />
												<path d="M7 22l-4-4 4-4" />
												<path d="M21 13v2a3 3 0 0 1-3 3H3" />
											</svg>
											Turno fijo
										</span>
									)}
								</div>
								<p className="zr-modal__text ba-modal__summary">
									<strong>{selectedReservation.name || "-"}</strong>
									<br />
									Cód. #{selectedReservation.reservationCode || "-"}
									<br />
									{formatDateWithDay(selectedReservation.date)} ·{" "}
									{selectedReservation.slotStart || "-"}
									{selectedReservation.slotEnd
										? ` a ${selectedReservation.slotEnd}hs`
										: ""}
									{selectedReservation.durationMinutes
										? ` (${duracionLabel(selectedReservation.durationMinutes)})`
										: ""}
								</p>
								<CourtReservationDetailRows
									reservation={selectedReservation}
									onVerSeries={() => {
										setActionConfirm(null);
										setSeriesListOpen(true);
									}}
									onCancelOpenMatch={() =>
										setActionConfirm({
											type: "cancelMatch",
											reservation: selectedReservation,
											origin: actionConfirm.origin,
										})
									}
								/>
								<div className="zr-modal__actions zr-modal__actions--reservation-detail">
									<button
										type="button"
										className="zr-btn zr-btn--ghost zr-modal__action"
										onClick={() => setActionConfirm(null)}
									>
										Volver
									</button>
									<button
										type="button"
										className="zr-btn zr-btn--soft zr-modal__action"
										onClick={() => setFichaDe(selectedReservation._id)}
									>
										Ver ficha
									</button>
									{["pending", "reserved"].includes(
										selectedReservation.status,
									) && (
										<button
											type="button"
											className="zr-btn zr-btn--dark zr-modal__action"
											disabled={actionLoading}
											onClick={() =>
												handleConfirm(
													selectedReservation._id,
													actionConfirm.origin === "grid",
												)
											}
										>
											Confirmar reserva
										</button>
									)}
									{selectedReservationViewState?.isCheckInEligible && (
										<button
											type="button"
											className="zr-btn zr-btn--dark zr-modal__action zr-reservation-action"
											disabled={actionLoading}
											onClick={() =>
												setActionConfirm({
													type: "checkin",
													reservation: selectedReservation,
													origin: actionConfirm.origin,
												})
											}
										>
											Marcar ingreso
										</button>
									)}
									{!selectedReservationViewState?.isCheckedIn &&
										!["cancelled", "expired", "no_show"].includes(
											selectedReservation.status,
										) &&
										!selectedReservationViewState?.isPast && (
											<button
												type="button"
												className="zr-pill-btn zr-modal__action zr-modal__action--danger"
												onClick={() =>
													setActionConfirm({
														type: "cancel",
														reservation: selectedReservation,
														origin: actionConfirm.origin,
													})
												}
											>
												Cancelar reserva
											</button>
										)}
									{!selectedReservationViewState?.isCheckedIn &&
										!["cancelled", "expired", "no_show"].includes(
											selectedReservation.status,
										) && (
										<button
											type="button"
											className="zr-pill-btn zr-modal__action zr-modal__action--danger"
											onClick={() =>
												setActionConfirm({
													type: "noshow",
													reservation: selectedReservation,
													origin: actionConfirm.origin,
												})
											}
										>
											Marcar No Show
										</button>
									)}
								</div>
							</>
						) : actionConfirm.type === "block" ? (
							<>
								<h3 id="zr-action-modal-title" className="zr-modal__title">
									Detalle del bloqueo
								</h3>
								<p className="zr-modal__text">
									Motivo:{" "}
									<strong>{actionConfirm.block.reason || "Sin motivo"}</strong>
								</p>
								{actionBlockGroupSize > 1 && (
									<p className="zr-modal__text">
										{blockTypeMeta(actionConfirm.block.type).label}
										{blockGroupRange(actionConfirm.block)
											? ` · ${blockGroupRange(actionConfirm.block)}`
											: ""}
										<br />
										Es parte de un bloqueo mayor ({actionBlockGroupSize} en
										total). ¿Querés eliminar solo este o todo el bloqueo?
									</p>
								)}
								<div className="zr-modal__actions">
									<button
										type="button"
										className="zr-btn zr-btn--ghost"
										onClick={() => setActionConfirm(null)}
										disabled={actionLoading}
									>
										Volver
									</button>
									{actionBlockGroupSize > 1 ? (
										<>
											<button
												type="button"
												className="zr-btn zr-btn--dark"
												onClick={() => handleActionExecute()}
												disabled={actionLoading}
											>
												{actionLoading ? "Procesando..." : "Solo este"}
											</button>
											<button
												type="button"
												className="zr-btn zr-btn--dark"
												onClick={() => handleActionExecute({ blockScope: "group" })}
												disabled={actionLoading}
											>
												{actionLoading
													? "Procesando..."
													: `Todo el bloqueo (${actionBlockGroupSize})`}
											</button>
										</>
									) : (
										<button
											type="button"
											className="zr-btn zr-btn--dark"
											onClick={handleActionExecute}
											disabled={actionLoading}
										>
											{actionLoading ? "Procesando..." : "Eliminar bloqueo"}
										</button>
									)}
								</div>
							</>
						) : actionConfirm.type === "seriesReport" ? (
							<>
								<h3 id="zr-action-modal-title" className="zr-modal__title">
									Turno fijo cancelado
								</h3>
								<p className="zr-modal__text">
									{seriesCancelledMessage(actionConfirm.result)}
								</p>
								<CourtSeriesKeptReport
									kept={actionConfirm.result.kept}
									intervalWeeks={actionConfirm.result.series?.intervalWeeks}
								/>
								<div className="zr-modal__actions">
									<button
										type="button"
										className="zr-btn zr-btn--dark"
										onClick={() => setActionConfirm(null)}
									>
										Entendido
									</button>
								</div>
							</>
						) : actionConfirm.type === "cancelMatch" ? (
							<>
								<h3 id="zr-action-modal-title" className="zr-modal__title">
									¿Cancelar partido abierto?
								</h3>
								<p className="zr-modal__text">
									Se cancela el partido de{" "}
									<strong>{actionConfirm.reservation.name || "-"}</strong>, se
									libera la cancha y se avisa a los jugadores anotados. Esta acción
									no se puede deshacer.
								</p>
								<div className="zr-modal__actions">
									<button
										type="button"
										className="zr-btn zr-btn--ghost"
										onClick={() => setActionConfirm(null)}
										disabled={actionLoading}
									>
										Volver
									</button>
									<button
										type="button"
										className="zr-btn zr-btn--dark"
										onClick={handleActionExecute}
										disabled={actionLoading}
									>
										{actionLoading ? "Procesando..." : "Confirmar"}
									</button>
								</div>
							</>
						) : actionReservationIsPresent &&
						  ["cancel", "noshow"].includes(actionConfirm.type) ? (
							<>
								<h3 id="zr-action-modal-title" className="zr-modal__title">
									Reserva ya presente
								</h3>
								<p className="zr-modal__text">
									Esta reserva ya figura como Presente y no puede cancelarse ni
									marcarse como No Show.
								</p>
								<div className="zr-modal__actions">
									<button
										type="button"
										className="zr-btn zr-btn--ghost"
										onClick={() => setActionConfirm(null)}
									>
										Volver
									</button>
								</div>
							</>
						) : (
							<>
								<h3 id="zr-action-modal-title" className="zr-modal__title">
									{actionConfirm.type === "cancel"
										? "¿Cancelar reserva?"
										: actionConfirm.type === "checkin"
											? "¿Marcar ingreso?"
											: "¿Marcar como No Show?"}
								</h3>
								<p className="zr-modal__text">
									¿Estás seguro de{" "}
									{actionConfirm.type === "cancel"
										? "cancelar"
										: actionConfirm.type === "checkin"
											? "marcar el ingreso"
											: "marcar como No Show"}{" "}
									la reserva de{" "}
									<strong>{actionConfirm.reservation.name || "-"}</strong> (Cód.
									#{actionConfirm.reservation.reservationCode})? Esta acción no
									se puede deshacer.
								</p>
								{actionOffersSeriesCancel && (
									<p className="zr-modal__text">
										Es un turno fijo: podés cancelar solo esta fecha o esta y
										todas las siguientes.
									</p>
								)}
								<div className="zr-modal__actions">
									<button
										type="button"
										className="zr-btn zr-btn--ghost"
										onClick={() => setActionConfirm(null)}
										disabled={actionLoading}
									>
										Volver
									</button>
									{actionOffersSeriesCancel ? (
										<>
											<button
												type="button"
												className="zr-btn zr-btn--dark"
												onClick={() => handleActionExecute()}
												disabled={actionLoading}
											>
												{actionLoading ? "Procesando..." : "Cancelar solo este"}
											</button>
											<button
												type="button"
												className="zr-btn zr-btn--dark"
												onClick={() =>
													handleActionExecute({ seriesScope: "following" })
												}
												disabled={actionLoading}
											>
												{actionLoading
													? "Procesando..."
													: "Cancelar este y los siguientes"}
											</button>
										</>
									) : (
										<button
											type="button"
											className="zr-btn zr-btn--dark"
											onClick={handleActionExecute}
											disabled={actionLoading}
										>
											{actionLoading ? "Procesando..." : "Confirmar"}
										</button>
									)}
								</div>
							</>
						)}
					</div>
				</div>
			)}
		</div>
	);
}

export default OrgReservationsScreen;
